import { type RequestContext } from '~/vendor/cx-web/domain-types';

import { type BettingInsightsContent, type BettingInsightsResult, type EsIndex } from '../types';

import { type BettingInsightsMainConfig, createBettingInsightsMain } from './BettingInsightsMain';
import { type BettingInsightsTableBodyConfig, createBettingInsightsTableBody } from './BettingInsightsTableBody';
import {
  type BettingInsightsTableCaptionConfig,
  createBettingInsightsTableCaption,
} from './BettingInsightsTableCaption';
import { type BettingInsightsTableCellConfig, createBettingInsightsTableCell } from './BettingInsightsTableCell';
import { type BettingInsightsTableFooterConfig, createBettingInsightsTableFooter } from './BettingInsightsTableFooter';
import {
  type BettingInsightsTableHeadingConfig,
  createBettingInsightsTableHeading,
} from './BettingInsightsTableHeading';

// Type for the context that will be passed as first parameter to getContent function
export interface BettingInsightsTableContext {
  isWalled: boolean;
  repubConfig: RequestContext['repubConfig'];
  userTZ: RequestContext['user']['TZ'];
}

export interface BettingInsightsTableConfig<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>> {
  body: BettingInsightsTableBodyConfig<TIndex, TContent>;
  caption?: BettingInsightsTableCaptionConfig<TIndex, TContent>;
  cells: BettingInsightsTableCellConfig<TIndex>[];
  footer?: BettingInsightsTableFooterConfig<TIndex>;
  getContent: (
    context: BettingInsightsTableContext,
    data: {
      result: BettingInsightsResult<TIndex>;
    },
  ) => BettingInsightsContent<TIndex>[];
  headings: {
    content: BettingInsightsTableHeadingConfig<TIndex>[];
    row?: number;
  };
  main?: BettingInsightsMainConfig<TIndex>;
  shouldToggle?: boolean;
  showAdditionalTitle?: boolean;
}

// Type alias for the return type of createBettingInsightsTable
export type BettingInsightsTable<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>> = Readonly<{
  body: ReturnType<typeof createBettingInsightsTableBody<TIndex, TContent>>;
  caption?: ReturnType<typeof createBettingInsightsTableCaption<TIndex, TContent>>;
  cells: ReturnType<typeof createBettingInsightsTableCell<TIndex>>[];
  footer?: ReturnType<typeof createBettingInsightsTableFooter<TIndex>>;
  getContent: (data: { result: BettingInsightsResult<TIndex> }) => BettingInsightsContent<TIndex>[];
  headings: {
    content: ReturnType<typeof createBettingInsightsTableHeading<TIndex>>[];
    row?: number;
  };
  isWalled: boolean;
  main?: ReturnType<typeof createBettingInsightsMain<TIndex>>;
  shouldToggle: boolean;
  showAdditionalTitle: boolean;
}>;

/**
 * Factory function to create BettingInsightsTable configuration (functional approach)
 * @param requestContext - The request context
 * @param config - The table configuration
 * @returns Immutable table configuration object
 */
export function createBettingInsightsTable<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>>(
  requestContext: RequestContext,
  config: BettingInsightsTableConfig<TIndex, TContent>,
): BettingInsightsTable<TIndex, TContent> {
  // Create context object
  const context: BettingInsightsTableContext = {
    repubConfig: requestContext.repubConfig,
    userTZ: requestContext.user.TZ,
    // Computed property (was a getter in the class)
    isWalled: requestContext.user.walls.length > 0,
  };

  // Create the result object with wrapper function that injects context
  const result = {
    // Context property needed by components
    isWalled: context.isWalled,

    // Config properties
    body: createBettingInsightsTableBody(requestContext, config.body),
    caption: config.caption ? createBettingInsightsTableCaption(requestContext, config.caption) : undefined,
    cells: config.cells.map(cell => createBettingInsightsTableCell(requestContext, cell)),
    footer: config.footer ? createBettingInsightsTableFooter(requestContext, config.footer) : undefined,
    headings: {
      row: config.headings.row,
      content: config.headings.content.map(heading => createBettingInsightsTableHeading(requestContext, heading)),
    },
    main: config.main ? createBettingInsightsMain(requestContext, config.main) : undefined,
    shouldToggle: config.shouldToggle ?? false,
    showAdditionalTitle: config.showAdditionalTitle ?? false,

    // Wrapper function that injects context as first parameter
    getContent: (data: { result: BettingInsightsResult<TIndex> }) => config.getContent(context, data),
  };

  return Object.freeze(result);
}
